<template>
  <base-modal :show="showModal" @controleModalApperance="$emit('closeModal')">
    <template #modal>
      <div class="modal_card">
        <div class="modal_header">
          <h5>{{ $t("walletRequests.acceptRequest") }}</h5>
          <button class="close_btn" @click="$emit('closeModal')">
            <i class="fal fa-times"></i>
          </button>
        </div>

        <div class="modal_body">
          <div class="accept_request_wrapper" v-if="requestData">
            <form @submit.prevent="submitForm">
              <div class="row">
                <!-- Start:: Request Info -->
                <div class="col-12 mb-3">
                  <div class="info_card">
                    <div class="info_row">
                      <label>{{ $t("walletRequests.requestId") }}:</label>
                      <span>#{{ requestData.id }}</span>
                    </div>
                    <div class="info_row">
                      <label>{{ $t("walletRequests.providerName") }}:</label>
                      <span>{{ requestData.user?.name }}</span>
                    </div>
                    <div class="info_row">
                      <label>{{ $t("walletRequests.requestedAmount") }}:</label>
                      <span class="font-weight-bold">{{
                        requestData.balance
                      }}</span>
                    </div>
                    <div class="info_row">
                      <label>{{ $t("walletRequests.currentBalance") }}:</label>
                      <span class="font-weight-bold">{{
                        requestData.balance
                      }}</span>
                    </div>
                  </div>
                </div>
                <!-- End:: Request Info -->

                <!-- Start:: Transfer Amount Input -->
                <base-input
                  col="12"
                  type="number"
                  :placeholder="$t('walletRequests.transferAmountLabel')"
                  v-model.number="formData.transfer_balance"
                  required
                  :min="0"
                  :max="requestData.balance"
                />
                <!-- End:: Transfer Amount Input -->

                <!-- Start:: New Balance Display -->
                <div class="col-12 mb-3" v-if="formData.transfer_balance">
                  <div class="alert alert-info">
                    <strong>{{ $t("walletRequests.newBalance") }}:</strong>
                    {{ calculatedNewBalance }}
                  </div>
                </div>
                <!-- End:: New Balance Display -->

                <!-- Start:: Notes Input -->
                <div class="col-12 mb-3">
                  <label class="d-block mb-2">{{
                    $t("walletRequests.notes")
                  }}</label>
                  <textarea
                    class="form-control"
                    rows="3"
                    :placeholder="$t('walletRequests.notesPlaceholder')"
                    v-model="formData.notes"
                  ></textarea>
                </div>
                <!-- End:: Notes Input -->
              </div>

              <!-- Start:: Submit Buttons -->
              <div class="form_submit_btn_wrapper">
                <button
                  type="submit"
                  class="btn btn-success"
                  :disabled="isSubmitting || !formData.transfer_balance"
                >
                  {{ $t("walletRequests.accept") }}
                </button>
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click="$emit('closeModal')"
                >
                  {{ $t("walletRequests.cancel") }}
                </button>
              </div>
              <!-- End:: Submit Buttons -->
            </form>
          </div>
        </div>
      </div>
    </template>
  </base-modal>
</template>

<script>
import BaseModal from "@/components/ui/BaseModal.vue";

export default {
  name: "AcceptWalletRequestModal",

  components: {
    BaseModal,
  },

  props: {
    showModal: {
      type: Boolean,
      required: true,
    },
    requestData: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      // Start:: Form Data
      formData: {
        transfer_balance: null,
        notes: "",
      },
      isSubmitting: false,
      // End:: Form Data
    };
  },

  computed: {
    calculatedNewBalance() {
      if (this.requestData && this.formData.transfer_balance) {
        return this.requestData.balance - this.formData.transfer_balance;
      }
      return 0;
    },
  },

  watch: {
    requestData: {
      handler(newVal) {
        if (newVal) {
          this.formData.transfer_balance = newVal.balance;
          this.formData.notes = "";
        }
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    // Start:: Submit Form
    async submitForm() {
      if (
        !this.formData.transfer_balance ||
        this.formData.transfer_balance <= 0
      ) {
        this.$message.error(this.$t("walletRequests.requiredField"));
        return;
      }

      if (this.formData.transfer_balance > this.requestData.balance) {
        this.$message.error(this.$t("errorOccurred"));
        return;
      }

      this.isSubmitting = true;
      try {
        await this.$axios({
          method: "POST",
          url: "wallets/submit-requests-wallet",
          data: {
            wallet_request_id: this.requestData.id,
            status: "accepted",
            transfer_balance: this.formData.transfer_balance,
            notes: this.formData.notes || undefined,
          },
        });

        this.$message.success(this.$t("walletRequests.acceptSuccess"));
        this.$emit("refreshData");
        this.$emit("closeModal");
        this.resetForm();
      } catch (error) {
        this.$message.error(this.walletErrorMessage(error));
      } finally {
        this.isSubmitting = false;
      }
    },
    // End:: Submit Form

    // Start:: Reset Form
    walletErrorMessage(error) {
      const message = error.response?.data?.message || "";
      const known = {
        "User bank details (IBAN/BIC) are missing. The wallet balance was left unchanged.":
          "walletRequests.missingBankDetails",
        "HyperSplit payout failed. The wallet balance was left unchanged.":
          "walletRequests.payoutFailed",
      };
      if (known[message]) return this.$t(known[message]);
      return message || this.$t("errorOccurred");
    },

    resetForm() {
      this.formData = {
        transfer_balance: null,
        notes: "",
      };
    },
    // End:: Reset Form
  },
};
</script>

<style scoped lang="scss">
.modal_card {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .modal_header {
    padding: 20px 25px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h5 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }

    .close_btn {
      background: none;
      border: none;
      font-size: 20px;
      cursor: pointer;
      color: #999;
      padding: 5px 10px;

      &:hover {
        color: #333;
      }
    }
  }

  .modal_body {
    overflow-y: auto;
    flex: 1;
  }
}

.accept_request_wrapper {
  padding: 20px;

  .info_card {
    background: #f5f5f5;
    border-radius: 8px;
    padding: 15px;

    .info_row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;

      &:last-child {
        margin-bottom: 0;
      }

      label {
        font-size: 14px;
        font-weight: 600;
        color: #666;
        margin: 0;
      }

      span {
        font-size: 14px;
        color: #333;
      }
    }
  }

  .form_submit_btn_wrapper {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    margin-top: 20px;
  }
}
</style>
